/**
 * CharacterCounter.tsx
 *
 * Universal character counter component for all chat platforms.
 * Displays current character count vs maximum allowed, with visual feedback.
 *
 * Features:
 * - Shows "X/1000" format
 * - Turns red when exceeding limit
 * - Works for all platforms (WhatsApp, TikTok, Shopee, Lazada)
 */

import { FC } from "react";

export interface CharacterCounterProps {
  /** Current message text */
  message: string;
  /** Maximum allowed characters (default: 1000) */
  maxLength?: number;
  /** Whether the counter should be disabled/grayed out */
  disabled?: boolean;
}

const CharacterCounter: FC<CharacterCounterProps> = ({
  message,
  maxLength = 1000,
  disabled = false,
}) => {
  const currentLength = message.length;
  const isOverLimit = currentLength > maxLength;

  return (
    <div
      className="d-flex justify-content-end px-2 py-1"
      style={{
        backgroundColor: disabled ? "#f5f5f5" : "transparent",
        borderRadius: disabled ? "0 0 10px 10px" : "0",
        borderBottom: "1px solid #cccccc",
        borderLeft: "1px solid #cccccc",
        borderRight: "1px solid #cccccc",
      }}
    >
      <small
        className={`fw-bold ${
          disabled
            ? "text-secondary"
            : isOverLimit
            ? "text-danger"
            : "text-muted"
        }`}
        style={{ opacity: disabled ? 0.5 : 1 }}
        data-testid="character-counter"
      >
        {currentLength}/{maxLength}
      </small>
    </div>
  );
};

export default CharacterCounter;
